import { useState, type FormEvent } from "react";
import { PMButton, PMField } from "@/ds/vendor";
import { AuthError, requestPasswordReset, signIn } from "@/auth/session";
import { Mark } from "@/ui/Mark";
import { Morph } from "@/ui/motion";
import farmer from "@/ds/vendor/assets/characters/Farmer_2.svg";
import cow from "@/ds/vendor/assets/characters/Cow 2.svg";
import bukkie from "@/ds/vendor/assets/characters/Bukkie.svg";
import chicken from "@/ds/vendor/assets/characters/Chicken.svg";
import "./signin.css";

// Login copy per README §7: app name + "Sign in to your account". No marketing voice.
export function SignIn({ onSignedIn, onDemo }: { onSignedIn: () => void; onDemo: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"signin" | "reset" | "sent">("signin");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email) return setError("Email is required");
    if (mode === "reset") {
      setBusy(true);
      await requestPasswordReset(email).catch(() => {});
      setBusy(false);
      return setMode("sent");
    }
    if (!password) return setError("Password is required");
    setBusy(true);
    try {
      await signIn(email, password);
      onSignedIn();
    } catch (err) {
      setError(err instanceof AuthError ? err.message : "Unable to reach the server. Check the connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <section className="signin-form">
        <div className="signin-brand"><Mark size={40} /><span>PropelMapper</span></div>
        <form onSubmit={submit} noValidate>
          <h1>{mode === "signin" ? "Sign in to your account" : "Reset password"}</h1>
          {mode === "sent" ? (
            <p className="signin-note">If an account exists for {email}, a reset link is on its way.</p>
          ) : (
            <>
              <PMField label="Email" type="email" value={email} onChange={(e: any) => setEmail(e.target.value)} autoFocus name="email" />
              {mode === "signin" && <PMField label="Password" type="password" value={password} onChange={(e: any) => setPassword(e.target.value)} name="password" />}
              {error && <div className="signin-error" role="alert">{error}</div>}
              <PMButton type="submit" block disabled={busy}>{busy ? "Signing in…" : mode === "signin" ? "Sign in" : "Send reset link"}</PMButton>
            </>
          )}
          <div className="signin-links">
            {mode === "signin" ? (
              <button type="button" className="linkbtn" onClick={() => setMode("reset")}>Forgot password</button>
            ) : (
              <button type="button" className="linkbtn" onClick={() => { setMode("signin"); setError(null); }}>← Back to sign in</button>
            )}
          </div>
        </form>
        <div className="signin-demo">
          <span>No account on this server?</span>
          <PMButton variant="outline" size="sm" onClick={onDemo}>Explore the demo team</PMButton>
        </div>
        <div className="signin-env">Connected to {import.meta.env.VITE_API_ORIGIN?.replace(/^https?:\/\//, "") || "dev.propelmapper.com"}</div>
      </section>
      <aside className="signin-hero" aria-hidden="false">
        <div className="signin-hero-inner">
          <span className="pm-chip-section signin-chip">FIELD NOTES, HANDLED</span>
          <h2>
            Every walk of the <Morph words={["north 80", "pivot corner", "orchard block", "paddock", "tract"]} />
            <br />becomes a signed report.
          </h2>
          <ul className="signin-steps">
            <li><span className="dot dot-photo" />Photos, voice memos and notes captured on the phone</li>
            <li><span className="dot dot-audio" />Transcribed and summarized while the truck is still moving</li>
            <li><span className="dot dot-text" />Reviewed, signed and sent from here</li>
          </ul>
        </div>
        <div className="signin-cast" aria-hidden="true">
          <img className="cast-bukkie" src={bukkie} alt="" />
          <img className="cast-farmer" src={farmer} alt="" />
          <img className="cast-cow" src={cow} alt="" />
          <img className="cast-chicken" src={chicken} alt="" />
          <i className="cast-ground" />
        </div>
      </aside>
    </div>
  );
}
